/**
 * Os ícones das condições dos tokens (BRAZ-25) e o Invisível (BRAZ-24): desenhos nossos, num
 * quadro de 24, em traço branco sobre um círculo com a cor da condição. No token, até 4 selos na
 * borda direita; passando disso, 3 e um "+N" (a lista toda fica na dica de cada um).
 */
import type { ReactNode } from 'react';
import { useT } from '../i18n/index.js';
import { TOKEN_CONDITIONS, type TokenCondition } from '../shared.js';
import './conditions.css';

export type ConditionId = TokenCondition | 'invisible';

const STYLE: Record<ConditionId, { color: string; glyph: ReactNode }> = {
  // olho riscado
  invisible: {
    color: '#1b1f24',
    glyph: (
      <>
        <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
        <circle cx="12" cy="12" r="3" />
        <path d="M3 3l18 18" />
      </>
    ),
  },
  // olho fechado, com os cílios
  blind: { color: '#5f6b78', glyph: <path d="M3 9.5c2.4 3.6 5.4 5.4 9 5.4s6.6-1.8 9-5.4M5.6 12.6 4 15M9.2 14.5l-.6 2.9M14.8 14.5l.6 2.9M18.4 12.6 20 15" /> },
  // caveira
  dead: {
    color: '#4a2c2c',
    glyph: (
      <>
        <path d="M12 3a7.5 7.5 0 0 0-7.5 7.5c0 2.6 1.3 4.2 2.5 5.1V19a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-3.4c1.2-.9 2.5-2.5 2.5-5.1A7.5 7.5 0 0 0 12 3zM10 20v-2.5M14 20v-2.5" />
        <circle cx="9" cy="11" r="1.6" fill="#fff" />
        <circle cx="15" cy="11" r="1.6" fill="#fff" />
      </>
    ),
  },
  // frasco com o líquido
  poisoned: { color: '#3c8d40', glyph: <path d="M9 3h6M10 3v5.5L5.2 17.3A2 2 0 0 0 7 20h10a2 2 0 0 0 1.8-2.7L14 8.5V3M7.6 14h8.8" /> },
  // espiral
  stunned: { color: '#b8860b', glyph: <path d="M12 12a1.75 1.75 0 0 1 3.5 0a3.5 3.5 0 0 1-7 0a5.25 5.25 0 0 1 10.5 0a7 7 0 0 1-14 0" /> },
  // raio
  paralyzed: { color: '#2c6db3', glyph: <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" /> },
  // seta até o chão
  prone: { color: '#8b6b3f', glyph: <path d="M12 4v11M7.5 10.5 12 15l4.5-4.5M5 20h14" /> },
  // rosto com medo: olhos arregalados, sobrancelhas para cima, boca tremida
  frightened: {
    color: '#a64ca6',
    glyph: (
      <>
        <path d="M6.5 7.5 9.5 6M17.5 7.5 14.5 6M7.5 17c1.5-1.5 3-1.5 4.5 0s3 1.5 4.5 0" />
        <circle cx="9" cy="11" r="1.6" />
        <circle cx="15" cy="11" r="1.6" />
      </>
    ),
  },
  // "Zz"
  unconscious: { color: '#4b4f9e', glyph: <path d="M4 5h7l-7 8h7M13 12h6l-6 7h6" /> },
  // gota
  bleeding: { color: '#c62f2f', glyph: <path d="M12 3c3.5 4.6 6 8 6 11a6 6 0 0 1-12 0c0-3 2.5-6.4 6-11z" fill="#fff" /> },
};

/** O nome da condição na língua de quem olha. */
export const useConditionLabel = () => {
  const t = useT();
  return (id: ConditionId) => (id === 'invisible' ? t('scene.invisible.badge') : t(`scene.cond.${id}`));
};

/** O desenho da condição dentro de um quadrado de lado `d`, a partir de (0, 0). */
function Badge({ id, d }: { id: ConditionId; d: number }) {
  return (
    <>
      <circle className="rd-cond__disc" cx={d / 2} cy={d / 2} r={d / 2} fill={STYLE[id].color} />
      <g className="rd-cond__glyph" transform={`translate(${d * 0.18} ${d * 0.18}) scale(${(d * 0.64) / 24})`}>
        {STYLE[id].glyph}
      </g>
    </>
  );
}

/** O ícone da condição para o menu e o diálogo (HTML). */
export function ConditionIcon({ id, size = 20 }: { id: ConditionId; size?: number }) {
  return (
    <svg className="rd-cond" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <Badge id={id} d={size} />
    </svg>
  );
}

/** As condições do token, na ordem em que aparecem: o Invisível primeiro, depois as da lista. */
export const tokenConditions = (token: { invisible: readonly string[] | null; conditions: readonly TokenCondition[] }): ConditionId[] => [
  ...(token.invisible ? (['invisible'] as const) : []),
  ...TOKEN_CONDITIONS.filter((id) => token.conditions.includes(id)),
];

/** Quantos selos cabem no token; passando disso, o último vira "+N". */
export const MAX_BADGES = 4;

/** Os selos que aparecem e quantas condições ficam no "+N". */
export function badgeSlots(ids: readonly ConditionId[]): { shown: ConditionId[]; more: ConditionId[] } {
  if (ids.length <= MAX_BADGES) return { shown: [...ids], more: [] };
  return { shown: ids.slice(0, MAX_BADGES - 1), more: ids.slice(MAX_BADGES - 1) };
}

/**
 * Onde fica o centro de cada selo, no token de lado `s` (origem no canto): na borda do círculo, do
 * alto à direita para baixo, sem um cobrir o outro.
 */
export function badgeCenters(count: number, s: number, d: number): { x: number; y: number }[] {
  const r = s / 2;
  const step = Math.max(33, (2 * Math.asin(Math.min(1, d / (2 * r))) * 180) / Math.PI);
  return Array.from({ length: count }, (_, i) => {
    const angle = ((40 + i * step) * Math.PI) / 180;
    return { x: r + r * Math.sin(angle), y: r - r * Math.cos(angle) };
  });
}

/** Os selos das condições no token (SVG, nas coordenadas do token). */
export function ConditionBadges({ ids, s, zoom }: { ids: readonly ConditionId[]; s: number; zoom: number }) {
  const label = useConditionLabel();
  if (!ids.length) return null;
  const d = Math.max(s * 0.28, 14 / zoom);
  const { shown, more } = badgeSlots(ids);
  const centers = badgeCenters(shown.length + (more.length ? 1 : 0), s, d);
  return (
    <g className="rd-token__conds" data-conds={ids.join(' ')}>
      {shown.map((id, i) => (
        <g key={id} className={`rd-token__cond rd-token__cond--${id}`} transform={`translate(${centers[i]!.x - d / 2} ${centers[i]!.y - d / 2})`} role="img" aria-label={label(id)}>
          <title>{label(id)}</title>
          <Badge id={id} d={d} />
        </g>
      ))}
      {more.length ? (
        <g className="rd-token__cond rd-token__cond--more" transform={`translate(${centers[shown.length]!.x - d / 2} ${centers[shown.length]!.y - d / 2})`} role="img" aria-label={more.map(label).join(', ')}>
          <title>{more.map(label).join(', ')}</title>
          <circle className="rd-cond__disc" cx={d / 2} cy={d / 2} r={d / 2} fill="#1b1f24" />
          <text x={d / 2} y={d / 2} fontSize={d * 0.48} dominantBaseline="central" textAnchor="middle">
            +{more.length}
          </text>
        </g>
      ) : null}
    </g>
  );
}
